import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Plus, X, Loader2 } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

import portalApi from '../lib/portalApi';
import { usePortalDestination } from '../PortalDestinationContext';

const NO_BRANCHES = [];
import { usePortalAuth } from '../PortalAuthContext';
import { PortalDocumentsCard } from './PortalDocumentsCard';
import { keyed } from '../../lib/listKeys';

const GENDERS = ['female', 'male', 'other', 'prefer_not_to_say'];
const BLOOD = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

function ChipList({ label, values, onChange, testId }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...values, v]);
    setDraft('');
  };
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-1.5">
        {keyed(values).map(({ item: v, key }, i) => (
          <span key={key} className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-full px-2.5 py-1 text-sm">
            {v}
            <button type="button" onClick={() => onChange(values.filter((_, j) => j !== i))} className="hover:text-red-600">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)}
          data-testid={testId}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={`Type and press Enter`} />
        <Button type="button" onClick={add} variant="outline"><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

export default function PortalProfilePage() {
  const [profileError, setProfileError] = useState('');
  const { pharmacy, branches = NO_BRANCHES } = usePortalDestination();
  const { patient, refresh, activeMember } = usePortalAuth();
  const [profile, setProfile] = useState(null);
  const [pharmacies, setPharmacies] = useState([]);
  const [preferred, setPreferred] = useState(patient?.preferred_tenant_id || '');
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState({ chat_reply_muted: false });

  useEffect(() => {
    let live = true;
    portalApi.get('/medical-profile').then(r => {
      if (!live) return;
      setProfile({
        dob: '', gender: '', blood_group: '',
        allergies: [], conditions: [], current_medications: [],
        emergency_contact: { name: '', phone: '', relation: '' },
        notes: '',
        ...(r.data || {}),
        emergency_contact: { name: '', phone: '', relation: '', ...((r.data || {}).emergency_contact || {}) },
      });
    }).catch(e => { if (live) setProfileError(e?.response?.data?.detail || e.message); });
    if (pharmacy) setPharmacies(branches);
    else portalApi.get('/pharmacies').then((r) => setPharmacies(r.data.pharmacies || [])).catch(() => {});
    portalApi.get('/notification-prefs')
      .then((r) => setPrefs({ chat_reply_muted: !!r.data?.chat_reply_muted }))
      .catch(() => {});
    return () => { live = false; };
  }, [pharmacy, branches]);

  const header = <h1 className="text-2xl font-bold text-slate-900" data-testid="portal-edit-profile-title">
    {activeMember ? `${activeMember.name}’s health profile` : 'Edit profile'}</h1>;

  if (profileError) return <div className="space-y-5" data-testid="portal-profile-page">{header}<p role="alert" data-testid="portal-secure-profile-error">{profileError}</p></div>;
  if (!profile) return <div className="space-y-5" data-testid="portal-profile-page">{header}<div className="py-10 text-center text-slate-400" data-testid="portal-secure-profile-loading"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div></div>;

  const save = async () => {
    setSaving(true);
    try {
      await portalApi.put('/medical-profile', profile);
      if (!activeMember) {
        if (preferred && preferred !== patient?.preferred_tenant_id) {
          await portalApi.put('/preferred-pharmacy', { tenant_id: preferred });
        }
        await portalApi.put('/notification-prefs', prefs);
        await refresh();
      }
      toast.success('Profile saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="portal-profile-page">
      {header}

      {!activeMember && <Card>
        <CardHeader><CardTitle>Preferred pharmacy</CardTitle></CardHeader>
        <CardContent>
          <Select value={preferred} onValueChange={setPreferred}>
            <SelectTrigger data-testid="portal-profile-pharmacy"><SelectValue placeholder="Choose a pharmacy" /></SelectTrigger>
            <SelectContent>
              {pharmacies.map((p) => (
                <SelectItem key={p.tenant_id} value={p.tenant_id}>{p.brand_name || p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 mt-2" data-testid="portal-preference-notice">Your saved preference does not change the service branch selected above.</p>
        </CardContent>
      </Card>}

      {!activeMember && <Card>
        <CardHeader><CardTitle>Notifications</CardTitle></CardHeader>
        <CardContent>
          <label className="flex items-start justify-between gap-4 cursor-pointer">
            <div className="min-w-0">
              <div className="font-medium text-slate-800">Pharmacist chat replies</div>
              <p className="text-xs text-slate-500 mt-0.5">
                Get an SMS + email when a pharmacist replies to your chat, so you don&apos;t have to keep the portal open.
              </p>
            </div>
            <input
              type="checkbox"
              className="mt-1 h-5 w-5 rounded accent-emerald-600 cursor-pointer"
              checked={!prefs.chat_reply_muted}
              onChange={(e) => setPrefs((p) => ({ ...p, chat_reply_muted: !e.target.checked }))}
              data-testid="portal-notify-chat-toggle"
            />
          </label>
        </CardContent>
      </Card>}

      <Card>
        <CardHeader><CardTitle>Basic info</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label>Date of birth</Label>
            <Input type="date" data-testid="portal-profile-dob" value={profile.dob || ''} onChange={(e) => setProfile({ ...profile, dob: e.target.value })} />
          </div>
          <div>
            <Label>Gender</Label>
            <Select value={profile.gender || ''} onValueChange={(v) => setProfile({ ...profile, gender: v })}>
              <SelectTrigger data-testid="portal-profile-gender"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {GENDERS.map((g) => <SelectItem key={g} value={g}>{g.replace(/_/g, ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Blood group</Label>
            <Select value={profile.blood_group || ''} onValueChange={(v) => setProfile({ ...profile, blood_group: v })}>
              <SelectTrigger data-testid="portal-profile-blood"><SelectValue placeholder="Unknown" /></SelectTrigger>
              <SelectContent>
                {BLOOD.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Clinical</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <ChipList label="Allergies" values={profile.allergies || []} onChange={(v) => setProfile({ ...profile, allergies: v })} testId="portal-profile-allergy-input" />
          <ChipList label="Chronic conditions" values={profile.conditions || []} onChange={(v) => setProfile({ ...profile, conditions: v })} testId="portal-profile-condition-input" />
          <ChipList label="Current medications" values={profile.current_medications || []} onChange={(v) => setProfile({ ...profile, current_medications: v })} testId="portal-profile-med-input" />
          <div>
            <Label>Notes for your pharmacist</Label>
            <Textarea rows={3} value={profile.notes || ''} onChange={(e) => setProfile({ ...profile, notes: e.target.value })} data-testid="portal-profile-notes" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Emergency contact</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label>Name</Label>
            <Input value={profile.emergency_contact?.name || ''} onChange={(e) => setProfile({ ...profile, emergency_contact: { ...profile.emergency_contact, name: e.target.value } })} data-testid="portal-ec-name" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={profile.emergency_contact?.phone || ''} onChange={(e) => setProfile({ ...profile, emergency_contact: { ...profile.emergency_contact, phone: e.target.value } })} data-testid="portal-ec-phone" />
          </div>
          <div>
            <Label>Relationship</Label>
            <Input value={profile.emergency_contact?.relation || ''} onChange={(e) => setProfile({ ...profile, emergency_contact: { ...profile.emergency_contact, relation: e.target.value } })} data-testid="portal-ec-relation" />
          </div>
        </CardContent>
      </Card>

      <PortalDocumentsCard />

      <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="portal-save-profile">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
        Save profile
      </Button>
    </div>
  );
}
